import type { Tel } from '@w/tel'
import type { WorkspaceDb } from '../db/workspace-tables'
import { updateWorkspacePageEmbeddings } from './page-embeddings'
import { NEMOTRON_EMBEDDING_INDEX } from './embedding-protocol'
import { EmbeddingRequestError, type ServerEmbeddings } from './server-embeddings.client'

const DEBOUNCE_MS = 1_000
const MAX_BACKOFF_MS = 5 * 60_000

/**
 * One run: every pending page is sent once. A page with no text is skipped; a page the server refuses as invalid is
 * reported once (`tel.error`, which carries the page and the server's reason to error tracking and the app's error
 * notification) and skipped for this owner's lifetime. Any other failure ends the run and is rethrown.
 */
export function updateServerPageEmbeddings(input: { db: WorkspaceDb; embeddings: ServerEmbeddings; tel: Tel; signal?: AbortSignal; skipped?: Set<string> }) {
  return input.tel.span({ name: 'embeddings.server.index', input: undefined }, tel => updateWorkspacePageEmbeddings({
    db: input.db, index: NEMOTRON_EMBEDDING_INDEX, skipped: input.skipped,
    // A page with no text has nothing to embed, and the server refuses blank text (embeddings.service.ts).
    accepts: text => text.trim() !== '',
    isClosed: () => Boolean(input.signal?.aborted) || !input.embeddings.canEmbed(),
    embeddings: { embedDocument: text => input.embeddings.embedDocument(text, tel, { signal: input.signal }) },
    isFinal(error, row) {
      if (!(error instanceof EmbeddingRequestError) || !error.final) return false
      const title = row.text.split('\n', 1)[0].trim() || 'Untitled page'
      tel.error(new Error(`Search could not index page “${title}”`, { cause: error }))
      return true
    },
  }).catch((error: unknown) => {
    if (input.signal?.aborted) return
    throw tel.error(error)
  }))
}

/** Separate lifetime and queue: server failures never disable local model maintenance.
 * A failed run backs off exponentially (2 s … 5 min); triggers during the backoff do not shorten it. */
export function createServerEmbeddingsBackground(input: { db: WorkspaceDb; embeddings: ServerEmbeddings; tel: Tel }) {
  const lifetime = new AbortController()
  const cancelled = Promise.withResolvers<void>()
  const skipped = new Set<string>()
  let failures = 0
  let timer: ReturnType<typeof setTimeout> | undefined
  let running = Promise.resolve()
  function flush() {
    if (timer === undefined) return
    clearTimeout(timer)
    timer = undefined
    running = running.then(run)
  }
  async function run() {
    if (lifetime.signal.aborted || !input.embeddings.canEmbed()) return
    try {
      await Promise.race([cancelled.promise, updateServerPageEmbeddings({ ...input, signal: lifetime.signal, skipped })])
      failures = 0
    } catch {
      // Already reported by updateServerPageEmbeddings; pending work stays queued for the retry.
      if (lifetime.signal.aborted) return
      failures += 1
      clearTimeout(timer)
      timer = setTimeout(flush, Math.min(DEBOUNCE_MS * 2 ** failures, MAX_BACKOFF_MS))
    }
  }
  function update() {
    if (lifetime.signal.aborted || !input.embeddings.canEmbed()) return
    if (failures > 0 && timer !== undefined) return
    clearTimeout(timer)
    timer = setTimeout(flush, DEBOUNCE_MS)
  }
  return { update, startBackfill() { update(); flush() },
    /** Joins the run in flight without starting a scheduled one. */
    settled() { return running },
    waitForIdle() { flush(); return running },
    close() { clearTimeout(timer); timer = undefined; lifetime.abort(); cancelled.resolve() },
  }
}
